import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import DatePicker from 'components/shared/form/DatePicker';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import { parseDate, simpleDashDate } from 'lib/date';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import PencilIcon from 'svg/PencilIcon';

import { ReferralFieldDef } from '../constants';

import { FieldLabel as Label, FieldRow as RowBase, FieldValueCell as ValueCell } from './FieldRow';

interface Option {
  label: string;
  value: string;
}

interface Props {
  field: ReferralFieldDef;
  value: string;
  onChange: (value: string) => void;
  options?: Option[];
  error?: string;
  // Which row is open belongs to the LIST, not to the row: a select or a date only leaves edit mode by
  // being changed, so a row opened and left alone used to stay open, and opening a second one left two
  // inputs on screen. The parent closes the previous row by turning this off.
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  // How the value reads when NOT being edited — the SSN passes its mask through here. Only the read
  // state is decorated: the edit control always shows the raw value, because asking someone to correct
  // a number they cannot read is the bug, not the feature.
  renderValue?: (value: string) => ReactNode;
}

// A ruled list, per the mock: a divider under every row and enough height that a label, a value, and
// a pencil read as one line rather than a dense block. `flex-start` rather than centered because a
// row can carry an error beneath its value, and centering the label against value-plus-error floats
// it into the gap between them.
//
// Row/Label/ValueCell themselves live in FieldRow.tsx, shared with the sync modal's read-only
// Selected Facility row — every editable field gets the divider, so it's defaulted on here rather
// than passed at each call site.
const Row = styled(RowBase).attrs({ $divider: true })``;

// The pencil sits at the row's right edge, not tucked against the value. Space-between rather than
// margin-left auto so the SSN's reveal toggle stays with the value it belongs to instead of drifting
// right with the pencil.
const ReadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Placeholder = styled(BodySmall)`
  color: ${colors.black25};
`;

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`;

const ErrorText = styled(BodySmall)`
  color: ${colors.red100};
`;

/**
 * One reviewable field: reads as a label/value row with a pencil, and swaps to the right control for
 * its type when edited (OLIO-2846). Net-new because every other PencilIcon in the app opens a modal or
 * slideout — this edits in place, inside a modal that is itself the review surface.
 *
 * Text commits on blur or Enter, Escape cancels; select and date commit on change. Blur is for text
 * ONLY: a Select's menu sits outside its input, so blurring there would unmount the control as the
 * option is clicked and lose the choice.
 */
export default function EditableField({
  field,
  value,
  onChange,
  options,
  error,
  isEditing,
  onStartEdit,
  onEndEdit,
  renderValue,
}: Props) {
  // `ssn` is rendered as a plain text box here, NOT via the entry form's SsnField (OLIO-2586).
  // Deliberate, and worth revisiting: SsnField stores digits-only and shields a packet partial behind
  // an explicit "enter the full number" gesture, which is arguably better than this field's
  // reveal-and-correct treatment of a redacted value. But adopting it changes this modal's SSN
  // contract (mask-while-reading, reveal-while-invalid, clear-to-unblock) rather than merely swapping
  // a control, so it is a follow-up rather than something to smuggle into a merge.
  const isTextual = field.type === 'text' || field.type === 'ssn';
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Re-seed when the committed value changes underneath (a refetch, or a reset on reopen) — but only
  // while not editing, so a refetch mid-edit can't yank what someone is typing.
  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && isTextual) inputRef.current?.focus();
  }, [isEditing, isTextual]);

  // Commit a pending draft when this row stops being the open one. Blur cannot be relied on for it:
  // the pencil that opens another row suppresses the focus change so the new input keeps focus, so
  // nothing blurs this input before it unmounts and the typing would be dropped.
  //
  // Text only. A select and a date commit their value straight through without touching the draft, so
  // for them this would write the pre-edit draft back over the value just picked.
  const wasEditing = useRef(isEditing);
  useEffect(() => {
    if (isTextual && wasEditing.current && !isEditing && draft !== value) onChange(draft);
    wasEditing.current = isEditing;
  }, [isTextual, isEditing, draft, value, onChange]);

  const commit = (next: string) => {
    // Keep the draft in step: a select and a date pass their value straight in, so without this the
    // draft still holds what was there before the pick.
    setDraft(next);
    onChange(next);
    onEndEdit();
  };

  const cancel = () => {
    setDraft(value);
    onEndEdit();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(draft);
    }
  };

  if (!isEditing) {
    return (
      <Row data-testid={`editable-field-${field.name}`}>
        <Label>
          {field.label}
          {field.optional ? ' (optional)' : ''}
        </Label>
        <ValueCell>
          <ReadRow>
            {value ? (
              <BodySmallBold data-testid={`value-${field.name}`}>
                {renderValue ? renderValue(value) : value}
              </BodySmallBold>
            ) : (
              <Placeholder data-testid={`value-${field.name}`}>Not provided</Placeholder>
            )}
            <EditButton
              type='button'
              // mousedown, because it fires BEFORE the open row's blur. Committing that row collapses
              // it, everything below shifts up ~18px, and a click whose mouseup lands elsewhere never
              // arrives — so pencil-to-pencil did nothing but commit. onClick stays for the keyboard,
              // and both are idempotent. preventDefault or mousedown's own focus handling lands after
              // the new input's autofocus and blurs it straight back out, closing the row as it opens.
              onMouseDown={(event) => {
                event.preventDefault();
                onStartEdit();
              }}
              onClick={onStartEdit}
              data-testid={`edit-${field.name}`}
              aria-label={`Edit ${field.label}`}>
              <PencilIcon />
            </EditButton>
          </ReadRow>
          {error && <ErrorText data-testid={`error-${field.name}`}>{error}</ErrorText>}
        </ValueCell>
      </Row>
    );
  }

  return (
    <Row data-testid={`editable-field-${field.name}`}>
      <Label>
        {field.label}
        {field.optional ? ' (optional)' : ''}
      </Label>
      <ValueCell onKeyDown={onKeyDown}>
        {isTextual && (
          <Input
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            // Clicking Send blurs this field, so without it the submit discards the correction.
            onBlur={() => commit(draft)}
            data-testid={`field-${field.name}`}
          />
        )}

        {field.type === 'select' && (
          <div data-testid={`field-${field.name}`}>
            <Select
              inputId={`field-${field.name}`}
              placeholder='Select'
              options={options ?? []}
              value={options?.find((option) => option.value === draft) ?? null}
              // Commits immediately: a select has no separate confirm gesture, and requiring one after
              // picking from a menu reads as the pick not having registered.
              onChange={(option) => commit((option as Option | null)?.value ?? '')}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
            />
          </div>
        )}

        {field.type === 'date' && (
          <div data-testid={`field-${field.name}`}>
            <DatePicker
              selected={parseDate(draft) ?? null}
              onChange={(date) => commit((date ? simpleDashDate(date) : '') ?? '')}
              $fullWidth
              placeholder='MM/DD/YYYY'
            />
          </div>
        )}

        {error && <ErrorText data-testid={`error-${field.name}`}>{error}</ErrorText>}
      </ValueCell>
    </Row>
  );
}
